import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { api, assetUrl, errMsg, telHref, mapHref } from "../lib/api";
import { Reveal, Countdown, fmtDateTime, fmtDate, daysLeft, daysLeftLabel } from "../lib/ui";
import { Nav, Footer, useLenis, scrollToId } from "../components/Chrome";
import { setMeta } from "../lib/seo";

const CATEGORIES = [
  ["Police & Government", "Retired patrol fleets, seized property and municipal surplus sold to the public.", "https://images.unsplash.com/photo-1517089152318-42ec560349c0?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000"],
  ["Heavy Equipment", "Excavators, loaders, skid steers, generators and contractor iron.", "https://images.unsplash.com/photo-1603814929877-d5d927322656?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000"],
  ["Vehicles & Trucks", "Cars, pickups, dump trucks, trailers and classic autos, titled & ready.", "https://images.unsplash.com/photo-1503708928676-1cb796a0891e?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000"],
  ["Industrial & Estate", "Machinery, tools, restaurant build-outs and full household estates.", "https://images.unsplash.com/photo-1740914994657-f1cdffdc418e?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000"],
];

const CHAPTERS = [
  ["01", "Licensed & bonded", "Every sale is run by a fully licensed Texas auctioneer. Clean titles, honest calls and clear terms — no surprises at the block."],
  ["02", "Live, onsite & simulcast", "Stand in the crowd or bid from your phone. We run true live auctions with real-time simulcast so distance never keeps you from the deal."],
  ["03", "Buyers & sellers", "From single-vehicle consignments to full municipal fleet liquidations, we move iron for cities, contractors, estates and everyday Texans."],
];

export default function Landing() {
  useLenis();
  const [settings, setSettings] = useState({});
  const [auctions, setAuctions] = useState({ upcoming: [], past: [] });
  const [locations, setLocations] = useState([]);
  const [challenge, setChallenge] = useState({ question: "", token: "" });
  const loc = useLocation();
  const navigate = useNavigate();
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const heroY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);

  const loadChallenge = () => api.get("/form-token").then((r) => setChallenge(r.data)).catch(() => {});

  useEffect(() => {
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
    api.get("/auctions").then((r) => setAuctions(r.data)).catch(() => {});
    setMeta({ image: window.location.origin + "/og-image.jpg" });
    api.get("/locations").then((r) => setLocations(r.data)).catch(() => {});
    loadChallenge();
  }, []);

  useEffect(() => {
    if (loc.hash) {
      const id = loc.hash.slice(1);
      setTimeout(() => scrollToId(id), 400);
    }
  }, [loc.hash]);

  const next = auctions.upcoming[0];
  const featured = auctions.upcoming.find((a) => a.featured);
  const line = { hidden: { y: "125%" }, show: (i) => ({ y: "0%", transition: { duration: 1.05, ease: [0.16, 1, 0.3, 1], delay: 0.15 + i * 0.12 } }) };

  return (
    <>
      <div className="grain" />
      <Nav />

      {/* HERO */}
      <section className="hero" id="top" ref={heroRef} data-testid="hero">
        <motion.div className="hero-media" style={{ y: heroY }}>
          <img src="/hero-dusk.jpg" alt="Texas auction yard at dusk" className="hero-img" />
          <div className="hero-veil" />
        </motion.div>
        <div className="hero-inner">
          <motion.div className="hero-eyebrow" initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1, duration: 0.8 }}>
            <span>{settings.hero_note || "Licensed & bonded Texas auctioneers since 1996"}</span>
          </motion.div>
          <h1 className="hero-title">
            <span className="line"><motion.span custom={0} variants={line} initial="hidden" animate="show" style={{ display: "block" }}>The gavel</motion.span></span>
            <span className="line"><motion.span custom={1} variants={line} initial="hidden" animate="show" style={{ display: "block" }}>drops in <em>Texas.</em></motion.span></span>
          </h1>
          <motion.p className="hero-sub" initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5, duration: 0.8 }}>
            Live, onsite &amp; simulcast auctions across the Dallas–Fort Worth metroplex — police fleets, heavy equipment, vehicles, machinery and full estates, sold to the highest bidder.
          </motion.p>
          <motion.div className="hero-cta" initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.65, duration: 0.8 }}>
            <button className="btn btn-amber" onClick={() => scrollToId("auctions")} data-testid="hero-view-auctions">View upcoming auctions</button>
            <button className="btn btn-ghost" onClick={() => scrollToId("consign")} data-testid="hero-consign">Consign an item</button>
          </motion.div>
        </div>
        {next && (
          <motion.div className="hero-next" initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 0.8 }} data-testid="next-auction-banner">
            <div className="hn-label">Next on the block</div>
            <div className="hn-title">{next.title}</div>
            <Countdown date={next.auction_date} seconds className="hn-count" label="Auction ends in" />
          </motion.div>
        )}
        <span className="hero-scroll">Scroll</span>
      </section>

      {/* MARQUEE */}
      <div className="marquee" aria-hidden="true">
        <div className="marquee-track">
          {[0, 1].map((k) => (
            <React.Fragment key={k}>
              <span>Police Fleet</span><span className="dot">◆</span><span>Government Surplus</span><span className="dot">◆</span>
              <span>Heavy Equipment</span><span className="dot">◆</span><span>Vehicles &amp; Trucks</span><span className="dot">◆</span>
              <span>Estate &amp; Machinery</span><span className="dot">◆</span><span>Live in Texas</span><span className="dot">◆</span>
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* FEATURED HIGHLIGHT */}
      {featured && (
        <section className="feat" data-testid="featured-banner">
          <Reveal>
            <div className="feat-inner" onClick={() => navigate(`/auction/${featured.id}`)} role="button">
              {assetUrl(featured.image) && (
                <div className="feat-bg" style={{ backgroundImage: `url(${assetUrl(featured.image)})` }} />
              )}
              <div className="feat-veil" />
              <div className="feat-content">
                <span className="feat-tag">★ Featured Sale</span>
                <h2 className="feat-title">{featured.title}</h2>
                <div className="feat-meta">{fmtDateTime(featured.auction_date)} &nbsp;◆&nbsp; {featured.location}</div>
                <Countdown date={featured.auction_date} seconds className="feat-count" label="Auction ends in" />
                <span className="btn btn-amber feat-btn">View this sale →</span>
              </div>
            </div>
          </Reveal>
        </section>
      )}

      {/* ABOUT */}
      <section className="section" id="about" data-testid="about">
        <div className="about-head">
          <Reveal><span className="kicker">Since {settings.years || "1996"} · Fort Worth, TX</span></Reveal>
          <Reveal delay={0.05}><h2 className="section-title">Three decades of<br />calling the bid.</h2></Reveal>
        </div>
        <div className="chapters">
          {CHAPTERS.map((c, i) => (
            <Reveal key={c[0]} delay={i * 0.08}>
              <article className="chapter">
                <span className="ch-num">{c[0]}</span>
                <h3>{c[1]}</h3>
                <p>{c[2]}</p>
              </article>
            </Reveal>
          ))}
        </div>
      </section>

      {/* AUCTIONS */}
      <section className="section" id="auctions" data-testid="auctions">
        <div className="sec-head">
          <Reveal><span className="kicker">The Calendar</span></Reveal>
          <Reveal delay={0.05}><h2 className="section-title">Upcoming auctions</h2></Reveal>
          <Reveal delay={0.1}><p className="sec-lead">Preview days, addresses and terms for every sale. Bring a trailer.</p></Reveal>
        </div>
        {auctions.upcoming.length === 0 ? (
          <Reveal><div className="empty" data-testid="auctions-empty">
            <p>No auctions are scheduled right now — new sales are added weekly.</p>
            <button className="btn btn-ghost" onClick={() => scrollToId("contact")}>Ask when we're next</button>
          </div></Reveal>
        ) : (
          <div className="auction-list">
            {auctions.upcoming.map((a, i) => {
              const featured = i === 0 && a.featured;
              const img = assetUrl(a.image);
              const dl = daysLeft(a.auction_date);
              const badge = daysLeftLabel(a.auction_date);
              return (
                <Reveal key={a.id} className={"auction-card" + (featured ? " featured" : "")} >
                  <div onClick={() => navigate(`/auction/${a.id}`)} style={{ display: "flex", flexDirection: featured ? "row" : "column", height: "100%" }} data-testid={`auction-card-${a.id}`}>
                    {img && <div className="ac-media"><img src={img} alt={a.title} loading="lazy" /></div>}
                    <div className="ac-body">
                      <div className="ac-daterow">
                        <div className="ac-date">{fmtDateTime(a.auction_date)}</div>
                        {badge && <span className={"ac-badge" + (dl === 0 ? " today" : dl <= 2 ? " urgent" : dl <= 7 ? " soon" : "")} data-testid={`days-left-${a.id}`}>{badge}</span>}
                      </div>
                      <h3 className="ac-title">{a.title}</h3>
                      <div className="ac-loc">◆ {a.location}</div>
                      {a.description && <p className="ac-desc">{a.description}</p>}
                      <div className="ac-foot">
                        <Countdown date={a.auction_date} className="ac-countdown" label="Auction ends in" />
                        <span className="ac-link">View lot &amp; photos →</span>
                      </div>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>
        )}
        {auctions.past.length > 0 && (
          <Reveal><div className="past-wrap">
            <h3 className="past-title">Recently sold</h3>
            <div className="past-list">
              {auctions.past.map((p) => (
                <div className="past-item" key={p.id} onClick={() => navigate(`/auction/${p.id}`)} style={{ cursor: "pointer" }} data-testid={`past-${p.id}`}>
                  <span className="mono">{fmtDate(p.auction_date)}</span>
                  <span className="pi-title">{p.title}</span>
                  <span className="pi-loc muted">{p.location}</span>
                </div>
              ))}
            </div>
          </div></Reveal>
        )}
      </section>

      {/* CATEGORIES */}
      <section className="section" id="categories" data-testid="categories">
        <div className="sec-head">
          <Reveal><span className="kicker">The Inventory</span></Reveal>
          <Reveal delay={0.05}><h2 className="section-title">What crosses the block</h2></Reveal>
        </div>
        <div className="cat-grid">
          {CATEGORIES.map((c, i) => (
            <Reveal key={c[0]} delay={i * 0.06}>
              <article className="cat-card" data-testid="category-card">
                <div className="cat-img"><img src={c[2]} alt="" loading="lazy" /></div>
                <div className="cat-veil" />
                <div className="cat-txt"><h3>{c[0]}</h3><p>{c[1]}</p></div>
              </article>
            </Reveal>
          ))}
        </div>
      </section>

      {/* LOCATIONS */}
      <section className="section" id="locations" data-testid="locations">
        <div className="sec-head">
          <Reveal><span className="kicker">Where We Sell</span></Reveal>
          <Reveal delay={0.05}><h2 className="section-title">Yards &amp; sale sites</h2></Reveal>
        </div>
        <div className="loc-grid">
          {locations.length === 0 ? (
            <p className="muted" data-testid="locations-empty">Locations are announced with each auction.</p>
          ) : locations.map((l, i) => {
            const full = [l.address, l.city, l.state, l.zip].filter(Boolean).join(", ");
            return (
              <Reveal key={l.id} delay={i * 0.05}>
                <article className="loc-card" data-testid={`location-card-${l.id}`}>
                  <h3>{l.name}</h3>
                  {full && <div className="loc-addr">{full}</div>}
                  {l.notes && <p className="loc-notes">{l.notes}</p>}
                  <div className="loc-foot">
                    {l.phone && <a href={telHref(l.phone)}>{l.phone}</a>}
                    <a href={mapHref(full || l.name)} target="_blank" rel="noopener noreferrer" className="loc-map">Directions ↗</a>
                  </div>
                </article>
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* ALERTS */}
      <section className="section" id="alerts" data-testid="alerts">
        <Reveal>
          <div className="alerts-inner">
            <span className="kicker">Never Miss A Sale</span>
            <h2 className="section-title sm">Get new-auction alerts</h2>
            <p className="sec-lead">Join the list and we'll email you the moment a fresh auction is posted — dates, locations and what's crossing the block.</p>
            <SubscribeForm />
          </div>
        </Reveal>
      </section>

      {/* FORMS */}
      <section className="section" id="consign" data-testid="forms">
        <div className="forms-grid">
          <div>
            <Reveal><span className="kicker">Sell With Us</span></Reveal>
            <Reveal delay={0.05}><h2 className="section-title sm">Consign an item</h2></Reveal>
            <Reveal delay={0.1}><p className="sec-lead">Got a vehicle, a machine or a whole estate? Tell us what you've got and we'll get it in front of ready buyers.</p></Reveal>
            <ConsignForm challenge={challenge} refresh={loadChallenge} />
          </div>
          <div id="contact" data-testid="contact">
            <Reveal><span className="kicker">Questions?</span></Reveal>
            <Reveal delay={0.05}><h2 className="section-title sm">Contact the yard</h2></Reveal>
            <Reveal delay={0.1}><p className="sec-lead">Call <a href={telHref(settings.phone)} className="inline-link">{settings.phone}</a> or drop us a line — we answer fast.</p></Reveal>
            <ContactForm challenge={challenge} refresh={loadChallenge} />
          </div>
        </div>
      </section>

      <Footer settings={settings} />
    </>
  );
}

function SubscribeForm() {
  const [email, setEmail] = useState("");
  const [website, setWebsite] = useState("");
  const [state, setState] = useState(null); // 'ok' | 'err' | null
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/subscribe", { email, website });
      setState("ok"); setEmail("");
    } catch { setState("err"); }
  };
  return (
    <>
      {state === "ok" && <div className="msg ok" data-testid="subscribe-success">You're on the list — watch your inbox for the next sale.</div>}
      {state === "err" && <div className="msg err" data-testid="subscribe-error">Please enter a valid email address and try again.</div>}
      <form className="sub-form" onSubmit={submit} data-testid="subscribe-form">
        <input type="text" className="hp" tabIndex={-1} autoComplete="off" value={website} onChange={(e) => setWebsite(e.target.value)} aria-hidden="true" />
        <input type="email" placeholder="you@email.com" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="subscribe-email" />
        <button type="submit" className="btn btn-amber" data-testid="subscribe-submit">Notify me</button>
      </form>
    </>
  );
}

function useForm(initial) {
  const [v, setV] = useState(initial);
  const on = (k) => (e) => setV((s) => ({ ...s, [k]: e.target.value }));
  return [v, on, setV];
}

function ContactForm({ challenge, refresh }) {
  const [v, on, setV] = useForm({ name: "", email: "", phone: "", message: "", human: "", website: "" });
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setMsg(null);
    try {
      await api.post("/contact", { ...v, form_token: challenge.token });
      setMsg({ ok: true, text: "Message received — thanks for reaching out. We'll reply shortly." });
      setV({ name: "", email: "", phone: "", message: "", human: "", website: "" });
      refresh();
    } catch (err) { setMsg({ ok: false, text: errMsg(err) }); refresh(); }
    setBusy(false);
  };
  return (
    <>
      {msg && <div className={"msg " + (msg.ok ? "ok" : "err")} data-testid={msg.ok ? "contact-success" : "contact-error"}>{msg.text}</div>}
      <form className="c-form" onSubmit={submit} data-testid="contact-form">
        <input type="text" className="hp" tabIndex={-1} value={v.website} onChange={on("website")} aria-hidden="true" />
        <input type="text" placeholder="Your name *" required value={v.name} onChange={on("name")} data-testid="contact-name" />
        <div className="two">
          <input type="email" placeholder="Email *" required value={v.email} onChange={on("email")} data-testid="contact-email" />
          <input type="tel" placeholder="Phone" value={v.phone} onChange={on("phone")} data-testid="contact-phone" />
        </div>
        <textarea rows={5} placeholder="How can we help? *" required value={v.message} onChange={on("message")} data-testid="contact-message" />
        <div className="human">
          <span className="hlbl">{challenge.question || "Loading…"} *</span>
          <input type="number" placeholder="Answer" required value={v.human} onChange={on("human")} data-testid="contact-human" />
        </div>
        <button type="submit" className="btn btn-amber" disabled={busy} data-testid="contact-submit">{busy ? "Sending…" : "Send message"}</button>
      </form>
    </>
  );
}

function ConsignForm({ challenge, refresh }) {
  const init = { fname: "", lname: "", email: "", phone: "", city: "", state: "TX", year: "", make: "", model: "", description: "", human: "", website: "" };
  const [v, on, setV] = useForm(init);
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setMsg(null);
    try {
      await api.post("/consignment", { ...v, form_token: challenge.token });
      setMsg({ ok: true, text: "Thanks — your consignment request is in. We'll be in touch within one business day." });
      setV(init); refresh();
    } catch (err) { setMsg({ ok: false, text: errMsg(err) }); refresh(); }
    setBusy(false);
  };
  return (
    <>
      {msg && <div className={"msg " + (msg.ok ? "ok" : "err")} data-testid={msg.ok ? "consign-success" : "consign-error"}>{msg.text}</div>}
      <form className="c-form" onSubmit={submit} data-testid="consign-form">
        <input type="text" className="hp" tabIndex={-1} value={v.website} onChange={on("website")} aria-hidden="true" />
        <div className="two">
          <input type="text" placeholder="First name *" required value={v.fname} onChange={on("fname")} data-testid="consign-fname" />
          <input type="text" placeholder="Last name" value={v.lname} onChange={on("lname")} data-testid="consign-lname" />
        </div>
        <div className="two">
          <input type="email" placeholder="Email *" required value={v.email} onChange={on("email")} data-testid="consign-email" />
          <input type="tel" placeholder="Phone *" required value={v.phone} onChange={on("phone")} data-testid="consign-phone" />
        </div>
        <div className="two">
          <input type="text" placeholder="City" value={v.city} onChange={on("city")} data-testid="consign-city" />
          <input type="text" placeholder="State" value={v.state} onChange={on("state")} data-testid="consign-state" />
        </div>
        <div className="three">
          <input type="text" placeholder="Year" value={v.year} onChange={on("year")} data-testid="consign-year" />
          <input type="text" placeholder="Make" value={v.make} onChange={on("make")} data-testid="consign-make" />
          <input type="text" placeholder="Model" value={v.model} onChange={on("model")} data-testid="consign-model" />
        </div>
        <textarea rows={3} placeholder="Describe the item(s), condition, VIN / hours, reserve…" value={v.description} onChange={on("description")} data-testid="consign-desc" />
        <div className="human">
          <span className="hlbl">{challenge.question || "Loading…"} *</span>
          <input type="number" placeholder="Answer" required value={v.human} onChange={on("human")} data-testid="consign-human" />
        </div>
        <button type="submit" className="btn btn-amber" disabled={busy} data-testid="consign-submit">{busy ? "Submitting…" : "Submit consignment"}</button>
      </form>
    </>
  );
}
